import React, {
	createContext,
	useContext,
	useEffect,
	useState,
	ReactNode,
} from 'react'
import { serverStatusService } from '@app/lib/services/serverStatusService'

interface ServerStatusContextType {
	isUpdating: boolean
	isPolling: boolean
	error: string | null
	startPolling: () => void
	stopPolling: () => void
	clearOverride: () => void
	isOverridden: boolean
}

const ServerStatusContext = createContext<ServerStatusContextType | undefined>(
	undefined
)

interface ServerStatusProviderProps {
	children: ReactNode
	pollInterval?: number
	autoStart?: boolean
}

export const ServerStatusProvider: React.FC<ServerStatusProviderProps> = ({
	children,
	pollInterval = 30000, // Default 30 seconds
	autoStart = true,
}) => {
	const [isUpdating, setIsUpdating] = useState(false)
	const [isPolling, setIsPolling] = useState(false)
	const [error, setError] = useState<string | null>(null)
	const [isOverridden, setIsOverridden] = useState(false)

	// Check localStorage override
	const checkLocalStorageOverride = (): boolean => {
		if (typeof window === 'undefined') return false
		try {
			const override = localStorage.getItem('updating')
			return override === 'false'
		} catch (error) {
			console.warn('Failed to read localStorage:', error)
			return false
		}
	}

	// Clear override from localStorage
	const clearOverride = () => {
		if (typeof window === 'undefined') return
		try {
			localStorage.removeItem('updating')
			setIsOverridden(false)
		} catch (error) {
			console.warn('Failed to clear localStorage:', error)
		}
	}

	const startPolling = () => {
		if (isPolling) return

		setIsPolling(true)
		setError(null)

		serverStatusService.startPolling(
			pollInterval,
			(status) => {
				// Don't show overlay if localStorage override is active
				const currentOverride = checkLocalStorageOverride()
				setIsUpdating(currentOverride ? false : status.updating)
				setError(null)
			},
			(error) => {
				setIsUpdating(false)
				console.error('Server status polling error:', error)
				setError(error.message)
				// Don't stop polling on error, keep trying
			}
		)
	}

	const stopPolling = () => {
		setIsPolling(false)
		serverStatusService.stopPolling()
	}

	// Check localStorage on component mount
	useEffect(() => {
		if (typeof window !== 'undefined') {
			const override = checkLocalStorageOverride()
			setIsOverridden(override)
		}
	}, [])

	useEffect(() => {
		if (autoStart && typeof window !== 'undefined') {
			startPolling()
		}

		return () => {
			stopPolling()
		}
	}, [autoStart, pollInterval])

	const contextValue: ServerStatusContextType = {
		isUpdating,
		isPolling,
		error,
		startPolling,
		stopPolling,
		clearOverride,
		isOverridden,
	}

	return (
		<ServerStatusContext.Provider value={contextValue}>
			{children}
		</ServerStatusContext.Provider>
	)
}

export const useServerStatus = (): ServerStatusContextType => {
	const context = useContext(ServerStatusContext)
	if (context === undefined) {
		throw new Error(
			'useServerStatus must be used within a ServerStatusProvider'
		)
	}
	return context
}
